import { createTheme } from '@mui/material/styles';
import pallet from './pallet';
import parentTypography from './typography';
import { fontSizes } from './fontSizes';

const theme = createTheme({
  palette: {
    primary: {
      main: pallet.primary,
      light: pallet.B50
    },
    success: {
      main: pallet.G400,
      light: pallet.G50
    },
    warning: {
      main: pallet.Y400,
      light: pallet.Y50
    },
    error: {
      main: pallet.R400,
      light: pallet.R50
    },
    background: {
      default: pallet.N20,
      paper: pallet.N0
    },
    text: {
      primary: pallet.textDefault,
      secondary: pallet.N100,
      disabled: pallet.DS50
    },
    divider: pallet.N40
  },
  shape: {
    borderRadius: 8
  },
  typography: parentTypography,
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          fontFamily: `'Satoshi-Medium', 'Roboto' !important`
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px 20px 16px 20px',
          '&:last-child': {
            paddingBottom: '16px'
          }
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        gutters: {
          paddingLeft: '16px',
          paddingRight: '16px',
          fontSize: fontSizes.h5
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          height: '35px',
          width: '35px'
        }
      }
    },
    MuiDialog: {
      defaultProps: {
        BackdropProps: {
          sx: {
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)'
          }
        }
      },
      styleOverrides: {
        root: {
          zIndex: '1500 !important'
        },
        paper: {
          margin: '32px 16px'
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0px 24px 24px'
        },
        spacing: {
          '&> :not(:first-child)': {
            marginLeft: '16px'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: '15px'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          textTransform: 'none',
          height: '40px',
          minWidth: '100px',
          padding: '10px 20px',
          color: pallet.N0,
          backgroundColor: pallet.primary,
          fontSize: fontSizes.default
        },
        contained: {
          borderRadius: '6px',
          backgroundColor: pallet.primary,
          color: pallet.N0,
          boxShadow: `0px 2px 4px 0px ${pallet.buttonShadow}`,
          '&:hover': {
            backgroundColor: pallet.primaryHover
          },
          '@media (hover: none)': {
            '&:hover': {
              backgroundColor: `${pallet.primary} !important`,
              boxShadow: `0px 2px 4px 0px ${pallet.buttonShadow}`
            }
          },
          height: '40px',
          minWidth: '100px',
          padding: '10px 20px'
        },
        sizeSmall: {
          fontSize: fontSizes.default,
          fontWeight: 'normal',
          height: '40px',
          padding: '10px 20px',
          borderRadius: '6px'
        },
        sizeLarge: {
          fontSize: fontSizes.default,
          fontWeight: 'normal',
          height: '40px',
          padding: '10px 20px',
          borderRadius: '6px'
        },
        outlined: {
          fontWeight: 'normal',
          backgroundColor: pallet.N0,
          border: `1px solid ${pallet.primary}`,
          color: pallet.primary,
          height: '38px',
          maxWidth: '170px',
          padding: '10px 20px',
          borderRadius: '6px'
        },
        outlinedSizeSmall: {
          fontWeight: 'normal',
          backgroundColor: pallet.N0,
          border: `1px solid ${pallet.primary}`,
          color: pallet.primary,
          height: '38px',
          minWidth: '140px',
          padding: '10px 20px',
          borderRadius: '6px'
        },
        text: {
          backgroundColor: 'transparent',
          color: pallet.primary
        }
      },
      variants: []
    },
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          backgroundColor: pallet.primary,
          color: pallet.N0,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: pallet.primaryHover
          },
          height: '40px',
          minWidth: '100px',
          padding: '10px 20px'
        },
        extended: {
          borderRadius: '6px',
          backgroundColor: pallet.primary,
          color: pallet.N0,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: pallet.primaryHover
          },
          height: '40px',
          minWidth: '100px',
          padding: '10px 20px'
        },
        sizeSmall: {
          fontSize: fontSizes.default,
          fontWeight: 'normal',
          height: '40px',
          padding: '10px 20px',
          borderRadius: '6px'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          margin: '0',
          padding: '16px'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: pallet.hashtagModalChipBackground,
          '&:hover, &:focus': {
            backgroundColor: `${pallet.hashtagModalChipBackground} !important`
          }
        },
        outlined: {
          backgroundColor: `${pallet.primary} !important`,
          color: `${pallet.white} !important`,
          '&:hover, &:focus': {
            backgroundColor: `${pallet.primary} !important`,
            color: `${pallet.white} !important`
          }
        },
        clickable: {
          '&:active': {
            boxShadow: 'none'
          }
        },
        deleteIcon: {
          width: 'unset !important',
          height: 'unset !important'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: pallet.N0,
          color: pallet.textDefault,
          boxShadow: '0px 4px 8px 0px #f4f5f7'
        },
        colorSecondary: {
          backgroundColor: pallet.N20,
          color: pallet.textDefault,
          boxShadow: '0px 4px 8px 0px #f4f5f7'
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: pallet.primary
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: pallet.primary
          }
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          slotProps: {
            backdrop: {
              sx: {
                backgroundColor: 'transparent !important'
              }
            }
          }
        }
      },
      styleOverrides: {
        root: {
          height: '40px !important',
          minWidth: '170px !important'
        },
        select: {
          overflow: 'visible'
        },
        outlined: {
          padding: '9px 14px !important'
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        dense: {
          paddingTop: 8,
          paddingBottom: 8,
          borderRadius: '5px'
        },
        root: {
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: pallet.B50
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: '40px !important',
          minWidth: '200px !important',
          '& .Mui-disabled': {
            color: `${pallet.disabledGrey} !important`,
            '-webkit-text-fill-color': `${pallet.disabledGrey} !important`,
            background: pallet.disabled
          },
          '&.Mui-disabled .MuiSelect-icon': { display: 'none !important' }
        },
        input: {
          padding: '9px 14px !important',
          borderRadius: '8px',
          '&::placeholder': {
            fontSize: '13px'
          }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '48px'
        }
      }
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          marginRight: '0px'
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: `${fontSizes.placeHolder} !important`
        },
        asterisk: {
          color: `${pallet.red2} !important`
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          color: `${pallet.red2} !important`
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontSize: fontSizes.h6
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '36px'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '8px'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          zIndex: 1199
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.4
          }
        }
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        label: {
          fontSize: fontSizes.subtitle,
          fontWeight: 500,
          '&.Mui-selected': {
            fontSize: `${fontSizes.subtitle} !important`,
            fontWeight: 700,
            color: pallet.textDefault
          }
        }
      }
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          zIndex: '1500 !important'
        }
      }
    }
  }
});

export default theme;
